import React from "react";
import { Box, Typography, DialogActions } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { FormTextField } from "../../../generic/GenericTextFields";
import { GenericDivider } from "../../../generic/GenericDivider";
import { UploadImage } from "./UploadImage";
import { ProfileSetupButtons } from "../ProfileSetupButtons";
import { BIO_KEY, LINKS_KEY, VERIFY_FACE_ID_KEY } from "../../../../enums/profileSetupEnums";
import { saveCollabInterests, saveContentInterests } from "../../../../config/apisauce";
import { MAX_CHAR_BIO } from "../../../../enums/generalEnums";
import { InterestMultiSelectCheckbox } from "../../../Inputs/InterestMultiSelectCheckbox";
import DialogLayout from "./../../../layouts/DialogLayout";

// Validation schema using Yup
const validationSchema = Yup.object({
  bio: Yup.string()
    .required("Bio is required")
    .min(100, "Bio must be at least 100 characters long")
    .max(MAX_CHAR_BIO, `Bio cannot exceed ${MAX_CHAR_BIO} characters`),
  profilePhoto: Yup.mixed().nullable().required("Profile photo is required"),
  uploadedImages: Yup.array().min(1, "At least one image is required").required("Images are required"),
  collabInterest: Yup.array().test(
    "collab-interests-required",
    "Please select at least one interest in both SFW and NSFW, or mark 'Not Interested' for each.",
    function (value, context) {
      const { collab_interest_nsfw = [], collab_interest_sfw = [] } = context.parent;
      const hasSelectedCollab = collab_interest_nsfw.length > 0 && collab_interest_sfw.length > 0;
      return hasSelectedCollab;
    }
  ),
  contentInterest: Yup.array().test(
    "collab-interests-required",
    "Please select at least one interest in both SFW and NSFW, or mark 'Not Interested' for each.",
    function (value, context) {
      const { content_interest_nsfw = [], content_interest_sfw = [] } = context.parent;
      const hasSelectedCollab = content_interest_nsfw.length > 0 && content_interest_sfw.length > 0;
      return hasSelectedCollab;
    }
  ),
});

export const BioForm = (props) => {
  const { handleChangeForm, handleSubmit, initialUserDetail } = props;

  const submitInterests = async (userId, collabIds, contentIds) => {
    try {
      const promises = [];
      if (collabIds.length > 0) {
        promises.push(
          saveCollabInterests({
            user_id: userId,
            categories_id: collabIds,
          })
        );
      }
      if (contentIds.length > 0) {
        promises.push(
          saveContentInterests({
            user_id: userId,
            categories_id: contentIds,
          })
        );
      }
      if (promises.length > 0) {
        await Promise.all(promises);
      }
    } catch (error) {
      console.error("Error submitting interests:", error);
      throw new Error("Failed to save interests. Please try again.");
    }
  };

  const formik = useFormik({
    initialValues: {
      bio: initialUserDetail?.bio || "",
      profilePhoto: initialUserDetail?.profile_picture || null,
      uploadedImages: [],
      collab_interest_nsfw:
        initialUserDetail?.user?.collab_interest?.filter((item) => item?.type === "collab_interest_nsfw") || [],
      collab_interest_sfw:
        initialUserDetail?.user?.collab_interest?.filter((item) => item?.type === "collab_interest_sfw") || [],
      content_interest_nsfw:
        initialUserDetail?.user?.content_interest?.filter((item) => item?.type === "content_interest_nsfw") || [],
      content_interest_sfw:
        initialUserDetail?.user?.content_interest?.filter((item) => item?.type === "content_interest_sfw") || [],
    },
    validationSchema,
    onSubmit: async (values, { setErrors }) => {
      try {
        await submitInterests(
          initialUserDetail.user_id,
          [...values.collab_interest_nsfw, ...values.collab_interest_sfw].map((item) => item.id),
          [...values.content_interest_nsfw, ...values.content_interest_sfw].map((item) => item.id)
        );
        handleSubmit({ profile_picture_id: values.profilePhoto?.id, bio: values.bio }, VERIFY_FACE_ID_KEY);
      } catch (error) {
        setErrors({ submit: error.message });
      }
    },
  });

  return (
    <Box
      component="form"
      onSubmit={(e) => {
        e.preventDefault();
        if (!formik.isSubmitting) {
          formik.handleSubmit();
        }
      }}
    >
      <DialogLayout>
        <UploadImage
          uploadedImages={formik.values.uploadedImages}
          profilePhoto={formik.values.profilePhoto}
          currentPhotoId={initialUserDetail?.profile_picture_id}
          sectionValidations={Object.keys(formik.errors).filter((key) => ["profilePhoto", "uploadedImages"].includes(key))}
          setUploadedImages={(updater) => {
            const currentImages = formik.values.uploadedImages || [];
            const newImages = typeof updater === "function" ? updater(currentImages) : updater;
            formik.setFieldValue("uploadedImages", newImages);
          }}
          handleSubmit={handleSubmit}
          userId={initialUserDetail?.user?.id}
          setProfilePhoto={(photo) => formik.setFieldValue("profilePhoto", photo)}
        />
        <Box mt={2.5}>
          <FormTextField
            name="bio"
            label="Bio"
            value={formik.values.bio}
            placeholder="Tell us something about yourself..."
            multiline={true}
            rows={7}
            handleChange={(value) => {
              if (value.length <= MAX_CHAR_BIO) {
                formik.setFieldValue("bio", value);
              }
            }}
            onBlur={formik.handleBlur}
            error={formik.touched.bio && Boolean(formik.errors.bio)}
            helperText={formik.touched.bio && formik.errors.bio}
            fullWidth
          />
          <Typography variant="caption" color={MAX_CHAR_BIO - formik.values.bio.length <= 50 ? "error" : "textSecondary"}>
            {`${MAX_CHAR_BIO - formik.values.bio.length} characters remaining`}
          </Typography>
          <GenericDivider color="#DFE3E8" />
        </Box>

        <InterestMultiSelectCheckbox formik={formik} />
      </DialogLayout>
      <DialogActions>
        <ProfileSetupButtons
          handleBack={() => handleChangeForm(LINKS_KEY)}
          handleSubmit={() => {
            if (!formik.isSubmitting) {
              formik.handleSubmit();
            }
          }}
          currentForm={BIO_KEY}
          loading={formik.isSubmitting}
          submitText="Next"
        />
      </DialogActions>
    </Box>
  );
};
